import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from '../_core/_services/api.service';
import { WalletDetailsDto } from '../_core/_model/wallet';
import { NotificationService } from '../_core/_services/notification.service';
import { AuthService } from '../_core/_services/auth.service';
import { FormatService } from '../_core/_services/format';
import { WalletTierData } from '../_core/_model/compliance';
import {
  CadDepositInstructionsResponseDto,
  DepositChannelDto,
  DepositFormConfigResponseDto,
  InteracEmailResponseDto,
  VirtualAccountResponseDto
} from '@shared/_core/_model/deposits';
import { MiniUserDto } from '@shared/_core/_model/users';

@Component({
  selector: 'app-deposit',
  standalone: false,
  templateUrl: './deposit.html',
  styleUrl: './deposit.css',
})
export class Deposit implements OnInit, OnDestroy {
  loading = false;
  securityLoading = true;
  submitted = false;
  ready = true;
  error = '';

  pageBlocker = '';
  pageBlockerCode = '';

  walletId = '';
  walletData!: WalletDetailsDto;
  userData!: MiniUserDto | null;

  showDepositPinModal = false;
  depositPin = '';
  depositPinError = '';

  dataForm: FormGroup;
  remaining = 0;
  limit = 0;

  depositConfig: DepositFormConfigResponseDto | null = null;
  depositChannels: DepositChannelDto[] = [];
  selectedDepositChannelId = '';

  interacEmailRecord: InteracEmailResponseDto | null = null;
  cadInstructions: CadDepositInstructionsResponseDto | null = null;
  virtualAccounts: VirtualAccountResponseDto[] = [];
  virtualAccount: VirtualAccountResponseDto | null = null;

  interacEmail = '';
  gatewayLoading = false;
  gatewayActionLoading = false;
  interacEmailReady = false;
  refreshingVirtualAccount = false;

  private virtualAccountPollHandle: ReturnType<typeof setInterval> | null = null;
  private sub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private fn: FormatService,
    private notifyService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
  ) {
    this.dataForm = this.fb.group({
      amount: ['', Validators.required]
    });
  }

  get amount() { return this.dataForm.get('amount'); }

  ngOnInit(): void {
    this.walletId = this.route.snapshot.paramMap.get('walletId') || '';

    const amount = this.route.snapshot.queryParamMap.get('amount');
    if (amount) {
      this.amount?.setValue(amount);
    }

    this.sub = this.auth.getCurrentUser().subscribe({
      next: u => {
        this.userData = u;
        this.securityLoading = false;
      },
      error: () => {
        this.securityLoading = false;
      },
      complete: () => {
        this.securityLoading = false;
      }
    });

    this.getWalletData(this.walletId);
  }

  ngOnDestroy(): void {
    this.stopVirtualAccountPolling();
    this.sub?.unsubscribe();
  }

  getWalletData(walletId: string): void {
    this.loading = true;

    this.api.getSingleWallet(walletId).subscribe({
      next: (res) => {
        this.walletData = res.data;
        this.remaining = this.walletData.currencyRules?.usage?.remainingDeposit ?? 0;

        let limit = 0;
        if (this.walletData.currencyRules?.usage?.hasApprovedKyc) {
          const remainingDailyLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Deposit', 'remainingDailyLimit');
          const remainingMonthlyLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Deposit', 'remainingMonthlyLimit');
          const perTransactionLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Deposit', 'perTransactionLimit');

          const candidates = [remainingDailyLimit, remainingMonthlyLimit, perTransactionLimit]
            .filter((value): value is number => typeof value === 'number' && value > 0);

          limit = candidates.length ? Math.min(...candidates) : 0;
        } else {
          limit = this.remaining;
        }

        this.limit = Math.max(0, limit);

        const validators = [
          Validators.required,
          Validators.min(this.walletData.currencyRules?.minDeposit || 0)
        ];

        if (this.limit > 0) {
          validators.push(Validators.max(this.limit));
        }

        this.amount?.setValidators(validators);
        this.amount?.updateValueAndValidity();

        this.loadDepositChannels();
      },
      error: (e) => {
        const message = this.getApiErrorMessage(e, 'Failed to load wallet');
        this.applyBlockerFromError(e);
        this.error = message;
        this.notifyService.showError(message, 'Wallet Error');
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  loadDepositChannels(): void {
    if (!this.walletId) return;

    this.gatewayLoading = true;
    this.api.getDepositChannels(this.walletId).subscribe({
      next: (res) => {
        this.depositConfig = res.data;
        this.depositChannels = res.data?.channels || [];
        this.virtualAccounts = res.data?.virtualAccounts || [];

        const selectedStillExists = this.depositChannels.some(
          x => x.mappingId === this.selectedDepositChannelId
        );

        const defaultChannel = this.depositChannels.find(x => x.isDefault) || this.depositChannels[0] || null;
        const selected = selectedStillExists
          ? this.depositChannels.find(x => x.mappingId === this.selectedDepositChannelId) || null
          : defaultChannel;

        if (selected) {
          this.selectDepositChannel(selected, false);
        } else {
          this.selectedDepositChannelId = '';
          this.virtualAccount = null;
        }
      },
      error: (e) => {
        this.depositConfig = null;
        this.depositChannels = [];
        this.selectedDepositChannelId = '';
        this.virtualAccounts = [];
        this.virtualAccount = null;

        const message = this.getApiErrorMessage(e, 'No deposit channel is currently available for this wallet.');
        this.applyBlockerFromError(e);
        this.error = message;
      },
      complete: () => {
        this.gatewayLoading = false;
      }
    });
  }

  selectDepositChannel(channel: DepositChannelDto, clearErrors = true): void {
    if (!channel) return;

    this.selectedDepositChannelId = channel.mappingId;
    this.submitted = false;

    if (clearErrors) {
      this.error = '';
    }

    this.stopVirtualAccountPolling();
    this.cadInstructions = null;
    this.interacEmailReady = false;
    this.virtualAccount = null;

    if (this.isInteracChannel) {
      this.loadInteracDepositInfo();
      return;
    }

    if (this.isVirtualAccountChannel) {
      this.virtualAccount = this.findVirtualAccountForChannel(channel);
      if (this.virtualAccount && !this.isVirtualAccountReady(this.virtualAccount)) {
        this.startVirtualAccountPolling();
      }
    }
  }

  get selectedChannel(): DepositChannelDto | null {
    return this.depositChannels.find(x => x.mappingId === this.selectedDepositChannelId) || null;
  }

  get isOnlinePaymentChannel(): boolean {
    const channel = this.selectedChannel;
    return !!channel && !channel.isVirtualAccount &&
      channel.channelType !== 'virtual-account' &&
      channel.channelType !== 'instructions' &&
      channel.code !== 'interac';
  }

  get isInteracChannel(): boolean {
    return this.selectedChannel?.code === 'interac' || this.selectedChannel?.channelType === 'instructions';
  }

  get isVirtualAccountChannel(): boolean {
    return !!this.selectedChannel && (
      this.selectedChannel.isVirtualAccount ||
      this.selectedChannel.channelType === 'virtual-account' ||
      this.selectedChannel.code === 'virtual-account'
    );
  }

  get usesManualDeposit(): boolean {
    return this.isInteracChannel || this.isVirtualAccountChannel;
  }

  get hasDepositChannels(): boolean {
    return this.depositChannels.length > 0;
  }

  get currencyCode(): string {
    return this.walletData?.currency?.code?.toUpperCase() || '';
  }

  get isCadWallet(): boolean {
    return this.currencyCode === 'CAD';
  }

  get channelAmountEligible(): boolean {
    const channel = this.selectedChannel;
    const amount = Number(this.amount?.value || 0);
    if (!channel || amount <= 0) return true;

    if (channel.minAmount != null && amount < channel.minAmount) return false;
    if (channel.maxAmount != null && amount > channel.maxAmount) return false;
    return true;
  }

  get channelAmountMessage(): string {
    const channel = this.selectedChannel;
    const amount = Number(this.amount?.value || 0);
    if (!channel || amount <= 0) return '';

    if (channel.minAmount != null && amount < channel.minAmount) {
      return `${channel.displayName} requires a minimum deposit of ${channel.minAmount} ${this.currencyCode}.`;
    }

    if (channel.maxAmount != null && amount > channel.maxAmount) {
      return `${channel.displayName} allows a maximum deposit of ${channel.maxAmount} ${this.currencyCode} per transaction.`;
    }

    return '';
  }

  get canStartOnlineDeposit(): boolean {
    return this.isOnlinePaymentChannel
      && !this.pageBlocker
      && this.dataForm.valid
      && this.channelAmountEligible
      && this.checkLimit('Deposit')
      && !this.loading
      && !this.securityLoading
      && !this.gatewayActionLoading
      && !this.gatewayLoading;
  }

  get requiresDepositPin(): boolean {
    return !!this.userData?.accountPin?.requirePinForDeposits;
  }

  continue(): void {
    this.submitted = true;
    this.error = '';

    if (!this.selectedChannel) {
      this.notifyService.showError('Select a deposit method first.', 'Deposit');
      return;
    }

    if (!this.isOnlinePaymentChannel) {
      return;
    }

    if (this.securityLoading) {
      this.notifyService.showInfo('Account security settings are still loading.', 'Deposit');
      return;
    }

    if (this.dataForm.invalid || !this.channelAmountEligible) {
      return;
    }

    if (this.requiresDepositPin) {
      this.depositPin = '';
      this.depositPinError = '';
      this.showDepositPinModal = true;
      return;
    }

    this.initializePayment(this.buildDepositRequest(null));
  }

  initializePayment(data: any = {}): void {
    if (this.loading) return;

    this.loading = true;

    this.api.initializePayment(data).subscribe({
      next: (res) => {
        const payment = res.data;

        if (!payment) {
          const message = 'Empty payment response.';
          if (this.showDepositPinModal) {
            this.depositPinError = message;
            this.depositPin = '';
          }
          this.notifyService.showError(message, 'Payment Error');
          this.loading = false;
          return;
        }

        this.showDepositPinModal = false;
        this.depositPin = '';
        this.depositPinError = '';

        if ((payment.checkoutType === 'redirect' || payment.checkoutType === 'hosted-form') && payment.checkoutUrl) {
          window.location.href = payment.checkoutUrl;
          return;
        }

        if (payment.checkoutType === 'inline-sdk') {
          this.notifyService.showInfo('Inline payment flow is not yet wired on this page.', 'Payment');
          this.loading = false;
          return;
        }

        if (payment.checkoutType === 'manual') {
          this.notifyService.showSuccess(
            'Deposit request created successfully. Complete the transfer using the instructions returned by the provider.',
            'Deposit Created'
          );
          this.loading = false;
          return;
        }

        this.notifyService.showError('Unsupported payment flow returned by server.', 'Payment Error');
        this.loading = false;
      },
      error: (e) => {
        const message = this.getApiErrorMessage(e, 'Failed to initialize payment');
        this.applyBlockerFromError(e);
        this.error = message;

        if (this.showDepositPinModal) {
          this.depositPinError = message;
          this.depositPin = '';
        }

        this.notifyService.showError(
          message,
          message.toLowerCase().includes('kyc') ? 'KYC Required' : 'Payment Error'
        );
        this.loading = false;
      }
    });
  }

  authorizeDeposit(pin: string): void {
    this.depositPin = pin;
    this.depositPinError = '';
    this.initializePayment(this.buildDepositRequest(pin));
  }

  closeDepositPinModal(): void {
    if (this.loading) return;

    this.showDepositPinModal = false;
    this.depositPin = '';
    this.depositPinError = '';
  }

  private buildDepositRequest(accountPin: string | null): any {
    return {
      walletId: this.walletData.walletId,
      amount: Number(this.amount?.value || 0),
      redirectUrl: `${window.location.origin}/payment/callback`,
      accountPin,
      depositChannelId: this.selectedDepositChannelId || null
    };
  }

  loadInteracDepositInfo(): void {
    if (!this.isInteracChannel) return;

    this.gatewayLoading = true;
    this.interacEmailReady = false;
    this.cadInstructions = null;

    this.api.getInteracEmail().subscribe({
      next: (res) => {
        this.interacEmailRecord = res.data || null;
        this.interacEmail = this.interacEmailRecord?.email || '';

        if (this.interacEmailRecord) {
          this.loadCadInstructions();
        }
      },
      error: () => {
        this.interacEmailRecord = null;
        this.interacEmailReady = false;
        this.cadInstructions = null;
        this.gatewayLoading = false;
      },
      complete: () => {
        this.gatewayLoading = false;
      }
    });
  }

  onInteracEmailChanged(): void {
    if (!this.isInteracChannel) return;
    this.interacEmailReady = false;
    this.cadInstructions = null;
  }

  saveInteracEmail(): void {
    const email = (this.interacEmail || '').trim();

    if (!email) {
      this.notifyService.showError('Enter the Interac email you will send from.', 'Validation');
      return;
    }

    this.gatewayActionLoading = true;
    this.interacEmailReady = false;
    this.cadInstructions = null;

    this.api.saveInteracEmail({ email }).subscribe({
      next: (res) => {
        this.interacEmailRecord = res.data;
        this.interacEmail = res.data.email;
        this.notifyService.showSuccess('Interac email saved.', 'Deposit');
        this.loadCadInstructions();
      },
      error: (e) => {
        this.notifyService.showError(
          this.getApiErrorMessage(e, 'Failed to save Interac email.'),
          'Deposit Error'
        );
        this.applyBlockerFromError(e);
        this.gatewayActionLoading = false;
      },
      complete: () => {
        this.gatewayActionLoading = false;
      }
    });
  }

  loadCadInstructions(): void {
    this.api.getCadDepositInstructions().subscribe({
      next: (res) => {
        this.cadInstructions = res.data;
        this.interacEmailReady = !!this.cadInstructions?.interacEmail;
      },
      error: (e) => {
        this.interacEmailReady = false;
        this.notifyService.showError(
          this.getApiErrorMessage(e, 'Failed to load Interac deposit instructions.'),
          'Deposit Error'
        );
      }
    });
  }

  createVirtualAccount(): void {
    const channel = this.selectedChannel;
    if (!channel || !this.isVirtualAccountChannel) return;

    this.gatewayActionLoading = true;

    this.api.createVirtualAccountByChannel(channel.mappingId).subscribe({
      next: (res) => {
        this.upsertVirtualAccount(res.data);
        this.virtualAccount = res.data;
        this.notifyService.showSuccess(`${this.currencyCode} bank-transfer account is ready.`, 'Deposit');

        if (!this.isVirtualAccountReady(res.data)) {
          this.startVirtualAccountPolling();
        }
      },
      error: (e) => {
        this.notifyService.showError(
          this.getApiErrorMessage(e, 'Failed to create virtual account.'),
          'Deposit Error'
        );
        this.applyBlockerFromError(e);
        this.gatewayActionLoading = false;
      },
      complete: () => {
        this.gatewayActionLoading = false;
      }
    });
  }

  refreshVirtualAccount(showNotification = true): void {
    if (!this.virtualAccount || this.refreshingVirtualAccount) return;

    this.refreshingVirtualAccount = true;

    this.api.refreshVirtualAccountById(this.virtualAccount.id).subscribe({
      next: (res) => {
        this.upsertVirtualAccount(res.data);
        this.virtualAccount = res.data;

        if (showNotification) {
          this.notifyService.showSuccess('Virtual account refreshed.', 'Deposit');
        }

        if (this.isVirtualAccountReady(res.data)) {
          this.stopVirtualAccountPolling();
        }
      },
      error: (e) => {
        if (showNotification) {
          this.notifyService.showError(
            this.getApiErrorMessage(e, 'Failed to refresh virtual account.'),
            'Deposit Error'
          );
        }
        this.applyBlockerFromError(e);
      },
      complete: () => {
        this.refreshingVirtualAccount = false;
      }
    });
  }

  startVirtualAccountPolling(): void {
    if (!this.virtualAccount || this.isVirtualAccountReady(this.virtualAccount)) return;

    this.stopVirtualAccountPolling();
    this.virtualAccountPollHandle = setInterval(() => {
      if (!this.refreshingVirtualAccount && this.virtualAccount) {
        this.refreshVirtualAccount(false);
      }
    }, 15000);
  }

  stopVirtualAccountPolling(): void {
    if (this.virtualAccountPollHandle) {
      clearInterval(this.virtualAccountPollHandle);
      this.virtualAccountPollHandle = null;
    }
  }

  isVirtualAccountReady(account: VirtualAccountResponseDto | null): boolean {
    const status = account?.status?.toUpperCase() || '';
    return ['READY', 'COMPLETED', 'SUCCESSFUL', 'VERIFIED', 'ACTIVE'].includes(status);
  }

  private findVirtualAccountForChannel(channel: DepositChannelDto): VirtualAccountResponseDto | null {
    return this.virtualAccounts.find(x => x.depositChannelId === channel.mappingId)
      || this.virtualAccounts.find(x =>
        !x.depositChannelId &&
        x.providerCode?.toUpperCase() === channel.gateway?.code?.toUpperCase()
      )
      || null;
  }

  private upsertVirtualAccount(account: VirtualAccountResponseDto): void {
    const index = this.virtualAccounts.findIndex(x => x.id === account.id);
    if (index >= 0) {
      this.virtualAccounts[index] = account;
    } else {
      this.virtualAccounts = [...this.virtualAccounts, account];
    }
  }

  getLimitData(
    tierInfo: WalletTierData | null,
    activityType: string,
    key: 'remainingDailyLimit' | 'remainingMonthlyLimit' | 'perTransactionLimit' | 'dailyLimit' | 'monthlyLimit'
  ) {
    return this.remaining + this.fn.getLimitData(tierInfo, activityType, key);
  }

  checkLimit(activityType: string): boolean {
    return (this.remaining > 0) || this.fn.checkLimit(this.amount?.value, this.walletData, activityType);
  }

  get depositKycRequired(): boolean {
    return !!this.walletData &&
      this.walletData.currencyRules?.usage?.hasApprovedKycForJurisdiction === false;
  }

  get depositJurisdiction(): string {
    return this.walletData?.currencyRules?.jurisdictionCode ?? this.depositConfig?.jurisdictionCode ?? '';
  }

  get shouldShowSwitchJurisdictionStep(): boolean {
    const current = this.userData?.country?.default?.code?.toUpperCase() ?? '';
    return current !== this.depositJurisdiction.toUpperCase();
  }

  goToCompliance(): void {
    this.notifyService.showInfo(
      'Redirecting you to complete KYC verification.',
      'Compliance'
    );
    this.router.navigate(['/account'], {
      queryParams: {
        tab: 'tabCountryChange',
        jurisdiction: this.depositJurisdiction
      }
    });
  }

  copyText(value?: string | null): void {
    if (!value) return;

    navigator.clipboard?.writeText(value);
    this.notifyService.showSuccess('Copied to clipboard.', 'Copied');
  }

  private getApiErrorCode(e: any): string {
    return e?.error?.Error?.Code || e?.error?.error?.code || '';
  }

  private getApiErrorMessage(e: any, fallback: string): string {
    return e?.error?.Error?.Details || e?.error?.message || fallback;
  }

  private applyBlockerFromError(e: any): void {
    const code = this.getApiErrorCode(e);
    const message = this.getApiErrorMessage(e, 'This activity is temporarily unavailable.');

    if (
      code === 'COMPLIANCE_BLOCK' ||
      code === 'COUNTRY_CHANGE_COOLDOWN' ||
      code === 'KYC_REQUIRED' ||
      code === 'LIMIT_EXCEEDED'
    ) {
      this.pageBlockerCode = code;
      this.pageBlocker = message;
    }
  }
}
